import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  CatalogRoom,
  ConflictResolution,
  ExistingBlackoutDate,
  ManualRoom,
  RateConflictInfo,
  StopsellApplyBlock,
  StopsellApplyPayload,
  StopsellApplyRange,
  StopsellDateRange,
  StopsellExtraction,
} from '~/types/stopsell';

import { findRateConflict } from '~/utils/stopsellConflicts';
import { pluralizeToString } from '~/utils/stringUtils';

import StopsellManualRoomsSection from './StopsellManualRoomsSection';
import StopsellRoomGroupRow from './StopsellRoomGroupRow';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (payload: StopsellApplyPayload) => void;
  extraction: StopsellExtraction;
  fileName: string;
  catalogRooms: Array<CatalogRoom>;
  isBlackoutError?: boolean;
  isApplying?: boolean;
}

function rateConflictKey(roomIndex: number, dateIndex: number, rateId: string): string {
  return `${roomIndex}-${dateIndex}-${rateId}`;
}

function buildRateOperations(
  dateRange: StopsellDateRange,
  rates: Array<{ id: string }>,
  blackoutDates: Array<ExistingBlackoutDate>,
  getResolution: (rateId: string) => ConflictResolution | null,
): { blocksToDelete: Array<StopsellApplyBlock>; rangesToCreate: Array<StopsellApplyRange> } {
  const blocksToDelete: Array<StopsellApplyBlock> = [];
  const rangesToCreate: Array<StopsellApplyRange> = [];

  for (const rate of rates) {
    const conflict = findRateConflict(dateRange, rate.id, blackoutDates);
    const resolution = getResolution(rate.id);

    if (resolution === 'skipped') continue;
    if (conflict.status === 'covered_by_existing') continue;
    if (conflict.status !== 'clear' && (resolution === 'ignored' || !resolution)) continue;

    const range =
      conflict.status === 'partial_overlap' && resolution === 'extending' && conflict.mergedRange
        ? conflict.mergedRange
        : { start: dateRange.start, end: dateRange.end };

    if (
      (conflict.status === 'covers_existing' || conflict.status === 'partial_overlap') &&
      (resolution === 'overriding' || resolution === 'extending') &&
      conflict.existingBlock
    ) {
      blocksToDelete.push(conflict.existingBlock);
    }

    rangesToCreate.push({ ...range, roomRateId: rate.id });
  }

  return { blocksToDelete, rangesToCreate };
}

function initSelected(extraction: StopsellExtraction, catalogRooms: Array<CatalogRoom>): Array<Array<boolean>> {
  return extraction.rooms.map((room) => {
    const catalogRoom = catalogRooms.find((c) => c.id === room.roomTypeId);
    const blackoutDates = catalogRoom?.blackoutDates ?? [];
    const rates = catalogRoom?.roomRates ?? [];
    return room.dates.map((date) => {
      const hasActionableConflict = rates.some((rate) => {
        const conflict = findRateConflict(date, rate.id, blackoutDates);
        return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
      });
      return !hasActionableConflict;
    });
  });
}

export default function StopsellReviewModal({
  open,
  onClose,
  onApply,
  extraction,
  fileName,
  catalogRooms,
  isBlackoutError = false,
  isApplying = false,
}: Props) {
  const [selected, setSelected] = useState<Array<Array<boolean>>>(() => initSelected(extraction, catalogRooms));
  const [roomTypeOverrides, setRoomTypeOverrides] = useState<Record<number, string>>({});
  const [resolutions, setResolutions] = useState<Record<string, ConflictResolution>>({});
  const [manualResolutions, setManualResolutions] = useState<Record<string, ConflictResolution>>({});
  const [manualRooms, setManualRooms] = useState<Array<ManualRoom>>([]);

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setResolutions({});
      setManualResolutions({});
      setRoomTypeOverrides({});
      setSelected(initSelected(extraction, catalogRooms));
      setManualRooms([]);
    }
    prevOpenRef.current = open;
  }, [open, extraction, catalogRooms]);

  const rateConflicts = useMemo<Array<Array<Array<RateConflictInfo>>>>(
    () =>
      extraction.rooms.map((room, roomIndex) => {
        const roomTypeId = roomTypeOverrides[roomIndex] ?? room.roomTypeId;
        const catalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
        const blackoutDates = catalogRoom?.blackoutDates ?? [];
        const rates = catalogRoom?.roomRates ?? [];
        return room.dates.map((date) => rates.map((rate) => findRateConflict(date, rate.id, blackoutDates)));
      }),
    [extraction, catalogRooms, roomTypeOverrides],
  );

  const unresolvedConflicts = useMemo(() => {
    const extractedUnresolved = rateConflicts.reduce((sum, roomConflicts, roomIndex) => {
      const roomTypeId = roomTypeOverrides[roomIndex] ?? extraction.rooms[roomIndex]?.roomTypeId;
      const rates = catalogRooms.find((c) => c.id === roomTypeId)?.roomRates ?? [];
      return (
        sum +
        roomConflicts.reduce((dateSum, dateConflicts, dateIndex) => {
          if (!selected[roomIndex]?.[dateIndex]) return dateSum;
          return (
            dateSum +
            dateConflicts.filter((conflict, rateIndex) => {
              if (conflict.status === 'clear' || conflict.status === 'covered_by_existing') return false;
              const rateId = rates[rateIndex]?.id;
              if (!rateId) return false;
              return !resolutions[rateConflictKey(roomIndex, dateIndex, rateId)];
            }).length
          );
        }, 0)
      );
    }, 0);
    const manualUnresolved = manualRooms.reduce((sum, room, roomIndex) => {
      const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
      const blackoutDates = catalogRoom?.blackoutDates ?? [];
      const rates = catalogRoom?.roomRates ?? [];
      return (
        sum +
        room.dates.reduce((dateSum, date, dateIndex) => {
          if (!date.selected) return dateSum;
          return (
            dateSum +
            rates.filter((rate) => {
              const conflict = findRateConflict(date, rate.id, blackoutDates);
              if (conflict.status === 'clear' || conflict.status === 'covered_by_existing') return false;
              return !manualResolutions[rateConflictKey(roomIndex, dateIndex, rate.id)];
            }).length
          );
        }, 0)
      );
    }, 0);

    return extractedUnresolved + manualUnresolved;
  }, [
    rateConflicts,
    resolutions,
    selected,
    roomTypeOverrides,
    extraction,
    catalogRooms,
    manualRooms,
    manualResolutions,
  ]);

  const allSelected = selected.every((room) => room.every(Boolean));
  const anySelected =
    selected.some((room) => room.some(Boolean)) || manualRooms.some((room) => room.dates.some((d) => d.selected));
  const selectAllIndeterminate = anySelected && !allSelected;

  const extractedRoomTypeIds = useMemo(
    () =>
      new Set(
        extraction.rooms
          .map((room, roomIndex) => roomTypeOverrides[roomIndex] ?? room.roomTypeId)
          .filter((id): id is string => id !== null),
      ),
    [extraction.rooms, roomTypeOverrides],
  );

  const allSelectedRoomsMapped = extraction.rooms.every((room, roomIndex) => {
    const isRoomSelected = selected[roomIndex]?.some(Boolean);
    if (!isRoomSelected) return true;
    return !!(room.roomTypeId ?? roomTypeOverrides[roomIndex]);
  });

  const selectedRoomCount =
    selected.filter((room) => room.some(Boolean)).length +
    manualRooms.filter((room) => room.dates.some((d) => d.selected)).length;

  const selectedDateCount =
    selected.reduce((sum, room) => sum + room.filter(Boolean).length, 0) +
    manualRooms.reduce((sum, room) => sum + room.dates.filter((d) => d.selected).length, 0);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelected(extraction.rooms.map((room) => room.dates.map(() => checked)));
    },
    [extraction],
  );

  const handleSelectAllChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => handleSelectAll(e.target.checked),
    [handleSelectAll],
  );

  const handleRoomToggleAll = useCallback((roomIndex: number, value: boolean) => {
    setSelected((prev) => prev.map((room, roomIdx) => (roomIdx === roomIndex ? room.map(() => value) : room)));
  }, []);

  const handleDateChange = useCallback((roomIndex: number, dateIndex: number, value: boolean) => {
    setSelected((prev) =>
      prev.map((room, roomIdx) =>
        roomIdx === roomIndex ? room.map((v, dateIdx) => (dateIdx === dateIndex ? value : v)) : room,
      ),
    );
  }, []);

  const handleRoomTypeOverride = useCallback(
    (roomIndex: number, roomTypeId: string) => {
      setRoomTypeOverrides((prev) => ({ ...prev, [roomIndex]: roomTypeId }));

      setResolutions((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${roomIndex}-`)) {
            delete next[key];
          }
        });
        return next;
      });

      const newCatalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
      const newBlackoutDates = newCatalogRoom?.blackoutDates ?? [];
      const newRates = newCatalogRoom?.roomRates ?? [];
      const room = extraction.rooms[roomIndex];
      setSelected((prev) =>
        prev.map((roomSelected, roomIdx) => {
          if (roomIdx !== roomIndex) return roomSelected;
          return room.dates.map((date) => {
            const hasActionableConflict = newRates.some((rate) => {
              const conflict = findRateConflict(date, rate.id, newBlackoutDates);
              return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
            });
            return !hasActionableConflict;
          });
        }),
      );
    },
    [catalogRooms, extraction],
  );

  const handleResolve = useCallback(
    (roomIndex: number, dateIndex: number, rateId: string, status: ConflictResolution) => {
      setResolutions((prev) => ({ ...prev, [rateConflictKey(roomIndex, dateIndex, rateId)]: status }));
    },
    [],
  );

  const handleUndo = useCallback((roomIndex: number, dateIndex: number, rateId: string) => {
    setResolutions((prev) => {
      const next = { ...prev };
      delete next[rateConflictKey(roomIndex, dateIndex, rateId)];
      return next;
    });
  }, []);

  const handleAddManualRoom = useCallback((catalogRoomId: string, catalogRoomName: string) => {
    setManualRooms((prev) => [...prev, { catalogRoomId, catalogRoomName, dates: [] }]);
  }, []);

  const handleRemoveManualRoom = useCallback((roomIndex: number) => {
    setManualRooms((prev) => prev.filter((_, i) => i !== roomIndex));
  }, []);

  const handleAddManualDate = useCallback(
    (roomIndex: number, start: string, end: string) => {
      setManualRooms((prev) => {
        const room = prev[roomIndex];
        const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
        const blackoutDates = catalogRoom?.blackoutDates ?? [];
        const rates = catalogRoom?.roomRates ?? [];
        const hasActionableConflict = rates.some((rate) => {
          const conflict = findRateConflict({ start, end }, rate.id, blackoutDates);
          return conflict.status === 'covers_existing' || conflict.status === 'partial_overlap';
        });
        return prev.map((r, i) =>
          i === roomIndex ? { ...r, dates: [...r.dates, { start, end, selected: !hasActionableConflict }] } : r,
        );
      });
    },
    [catalogRooms],
  );

  const handleRemoveManualDate = useCallback((roomIndex: number, dateIndex: number) => {
    setManualRooms((prev) =>
      prev.map((room, i) =>
        i === roomIndex ? { ...room, dates: room.dates.filter((_, j) => j !== dateIndex) } : room,
      ),
    );
  }, []);

  const handleResolveManualDate = useCallback(
    (roomIndex: number, dateIndex: number, rateId: string, status: ConflictResolution) => {
      setManualResolutions((prev) => ({ ...prev, [rateConflictKey(roomIndex, dateIndex, rateId)]: status }));
    },
    [],
  );

  const handleUndoManualDate = useCallback((roomIndex: number, dateIndex: number, rateId: string) => {
    setManualResolutions((prev) => {
      const next = { ...prev };
      delete next[rateConflictKey(roomIndex, dateIndex, rateId)];
      return next;
    });
  }, []);

  const handleToggleManualDate = useCallback((roomIndex: number, dateIndex: number, selected: boolean) => {
    setManualRooms((prev) =>
      prev.map((room, i) =>
        i === roomIndex
          ? { ...room, dates: room.dates.map((d, j) => (j === dateIndex ? { ...d, selected } : d)) }
          : room,
      ),
    );
  }, []);

  const handleApply = useCallback(() => {
    type RoomOperation = StopsellApplyPayload['rooms'][number]['operations'][number];

    const payload: StopsellApplyPayload = {
      rooms: extraction.rooms
        .map((room, roomIndex) => {
          const roomTypeId = roomTypeOverrides[roomIndex] ?? room.roomTypeId;
          if (!roomTypeId) return null;

          const catalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
          const rates = catalogRoom?.roomRates ?? [];
          const blackoutDates = catalogRoom?.blackoutDates ?? [];

          const operations = room.dates.reduce<Array<RoomOperation>>((acc, date, dateIndex) => {
            if (!selected[roomIndex][dateIndex]) return acc;

            const { blocksToDelete, rangesToCreate } = buildRateOperations(
              date,
              rates,
              blackoutDates,
              (rateId) => resolutions[rateConflictKey(roomIndex, dateIndex, rateId)] ?? null,
            );

            if (rangesToCreate.length === 0) return acc;
            acc.push({ operation: 'blackout', blocksToDelete, rangesToCreate });
            return acc;
          }, []);

          if (operations.length === 0) return null;
          return { roomTypeId, operations };
        })
        .filter((room): room is NonNullable<typeof room> => room !== null),
    };

    const manualEntries = manualRooms
      .map((room, origRoomIndex) => ({ room, origRoomIndex }))
      .filter(({ room }) => room.dates.some((d) => d.selected))
      .map(({ room, origRoomIndex }) => {
        const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
        const rates = catalogRoom?.roomRates ?? [];
        const blackoutDates = catalogRoom?.blackoutDates ?? [];
        return {
          roomTypeId: room.catalogRoomId,
          operations: room.dates
            .map((d, origDateIndex) => ({ d, origDateIndex }))
            .filter(({ d }) => d.selected)
            .map(({ d, origDateIndex }) => {
              const { blocksToDelete, rangesToCreate } = buildRateOperations(
                d,
                rates,
                blackoutDates,
                (rateId) => manualResolutions[rateConflictKey(origRoomIndex, origDateIndex, rateId)] ?? null,
              );

              return { operation: 'blackout' as const, blocksToDelete, rangesToCreate };
            })
            .filter((op) => op.rangesToCreate.length > 0),
        };
      })
      .filter((room) => room.operations.length > 0);

    const extractedRoomTypeIdSet = new Set(payload.rooms.map((r) => r.roomTypeId));
    payload.rooms.push(...manualEntries.filter((e) => !extractedRoomTypeIdSet.has(e.roomTypeId)));

    onApply(payload);
  }, [extraction, onApply, roomTypeOverrides, selected, resolutions, manualRooms, manualResolutions, catalogRooms]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <EventBusyOutlinedIcon color="warning" />
          <Typography variant="h6">Stopsell Dates</Typography>
        </Stack>
        <Chip icon={<DescriptionOutlinedIcon />} label={fileName} size="small" sx={{ mt: 1 }} />
      </DialogTitle>

      <DialogContent dividers>
        {isBlackoutError ? (
          <Alert severity="error">Could not load existing blackout dates. Please close and try again.</Alert>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                mb: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectAllIndeterminate}
                    onChange={handleSelectAllChange}
                  />
                }
                label={<Typography fontWeight={500}>Select all rooms</Typography>}
              />
            </Box>

            <Stack gap={1.5}>
              {extraction.rooms.map((room, roomIndex) => (
                <StopsellRoomGroupRow
                  key={`${room.name}-${roomIndex}`}
                  roomIndex={roomIndex}
                  room={room}
                  selected={selected[roomIndex]}
                  catalogRooms={catalogRooms}
                  selectedRoomTypeId={roomTypeOverrides[roomIndex] ?? room.roomTypeId ?? ''}
                  resolutions={resolutions}
                  onDateChange={handleDateChange}
                  onToggleAll={handleRoomToggleAll}
                  onRoomTypeChange={handleRoomTypeOverride}
                  onResolve={handleResolve}
                  onUndo={handleUndo}
                />
              ))}
            </Stack>

            <StopsellManualRoomsSection
              catalogRooms={catalogRooms}
              extractedRoomTypeIds={extractedRoomTypeIds}
              manualRooms={manualRooms}
              manualResolutions={manualResolutions}
              onAddRoom={handleAddManualRoom}
              onRemoveRoom={handleRemoveManualRoom}
              onAddDate={handleAddManualDate}
              onRemoveDate={handleRemoveManualDate}
              onToggleDate={handleToggleManualDate}
              onResolve={handleResolveManualDate}
              onUndo={handleUndoManualDate}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Stack direction="row" gap={2} alignItems="center">
          {unresolvedConflicts > 0 && (
            <Typography variant="body2" color="warning.dark">
              <strong>
                {unresolvedConflicts} unresolved {unresolvedConflicts === 1 ? 'conflict' : 'conflicts'}
              </strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            <strong>{pluralizeToString('room', selectedRoomCount)}</strong>,{' '}
            <strong>{pluralizeToString('date range', selectedDateCount)}</strong> selected
          </Typography>
        </Stack>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip
            title={
              isBlackoutError
                ? 'Blackout dates could not be loaded. Please close and try again.'
                : anySelected && !allSelectedRoomsMapped
                ? 'All selected rooms must be mapped to a room type'
                : unresolvedConflicts > 0
                ? 'Resolve all conflicts before applying'
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={
                  isApplying || isBlackoutError || !anySelected || !allSelectedRoomsMapped || unresolvedConflicts > 0
                }
              >
                {isApplying ? 'Applying...' : 'Apply Stopsell'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
